/**
 * Emergencies a crew declares on the frequency. The sim decides that one happens and what it does
 * to the aircraft; the pilot brain says it, in the ICAO order (MAYDAY or PAN PAN three times, the
 * station, the callsign, the nature, the intentions, the position and level).
 *
 * ASSUMPTIONS (docs/sim-design.md): the rates, which aircraft are eligible, and the performance
 * penalties are tuned for play. A distress call is MAYDAY; an urgency call, where the flight is
 * safe but needs priority, is PAN PAN.
 */
import type { Aircraft } from './aircraft';
import { isOnGroundPhase, isRemovedPhase } from './aircraft';
import type { Rng } from './rng';

export type EmergencyNature = 'fuel' | 'engine' | 'medical' | 'pressurization' | 'birdStrike';
export type EmergencyLevel = 'mayday' | 'pan';
export type EmergencyIntention = 'landAsap' | 'priorityLanding' | 'emergencyDescent' | 'returnToLand';

export interface Emergency {
  nature: EmergencyNature;
  level: EmergencyLevel;
  intention: EmergencyIntention;
  startedAtS: number;
  /** Souls on board, when the controller asks. */
  persons: number;
  /** Set once the controller has said anything to the aircraft since the call. */
  acknowledgedAtS?: number;
}

/** How often an emergency begins, per hour of play. */
export const EMERGENCY_RATES = { off: 0, rare: 0.5, normal: 1.5 } as const;
export type EmergencyRate = keyof typeof EMERGENCY_RATES;
/** Chance that an arrival appears already short of fuel, by setting. */
export const TIGHT_FUEL_CHANCE = { off: 0, rare: 0.05, normal: 0.12 } as const;

const NATURE: Record<EmergencyNature, { level: EmergencyLevel; intention: EmergencyIntention }> = {
  engine: { level: 'mayday', intention: 'landAsap' },
  pressurization: { level: 'mayday', intention: 'emergencyDescent' },
  fuel: { level: 'mayday', intention: 'priorityLanding' },
  medical: { level: 'pan', intention: 'priorityLanding' },
  birdStrike: { level: 'pan', intention: 'returnToLand' },
};

/** What could plausibly happen to this aircraft right now. */
export function eligibleNatures(ac: Aircraft, airportElevationFt: number): EmergencyNature[] {
  if (isRemovedPhase(ac.phase) || isOnGroundPhase(ac.phase) || ac.emergency) return [];
  const agl = ac.altitudeFt - airportElevationFt;
  const out: EmergencyNature[] = ['engine'];
  if (ac.altitudeFt > 10000) out.push('pressurization');
  if (ac.kind === 'arrival' && ac.phase === 'enroute') out.push('medical');
  if (ac.kind === 'departure' && agl > 500 && agl < 5000 && ac.phaseT < 180) out.push('birdStrike');
  return out;
}

/** People on board, by size of aircraft: what the crew answers when asked for souls on board. */
export function personsFor(ac: Aircraft, rng: Rng): number {
  return ac.category === 'light' ? rng.int(1, 6) : ac.category === 'heavy' ? rng.int(180, 380) : rng.int(60, 190);
}

export function startEmergency(ac: Aircraft, nature: EmergencyNature, simTimeS: number, rng: Rng): Emergency {
  const { level, intention } = NATURE[nature];
  return { nature, level, intention, startedAtS: simTimeS, persons: ac.persons ?? personsFor(ac, rng) };
}

/** What the failure does to the aircraft. Kinematic only: the flight model keeps flying it. */
export function applyEmergencyPerformance(ac: Aircraft): void {
  if (ac.emergency?.nature !== 'engine') return;
  ac.perf = { ...ac.perf, climbFpmSeaLevel: ac.perf.climbFpmSeaLevel * 0.5, climbFpm20k: ac.perf.climbFpm20k * 0.5 };
  ac.expedite = false;
}

/** Plain words for the radio and the radar tag. */
export const NATURE_TEXT: Record<EmergencyNature, string> = {
  engine: 'engine failure',
  pressurization: 'cabin pressure problem',
  medical: 'medical emergency on board',
  birdStrike: 'bird strike',
  fuel: 'fuel emergency',
};
export const NATURE_CODE: Record<EmergencyNature, string> = { engine: 'ENG', pressurization: 'PRES', medical: 'MED', birdStrike: 'BIRD', fuel: 'FUEL' };
export const INTENTION_TEXT: Record<EmergencyIntention, string> = {
  landAsap: 'request immediate landing runway',
  priorityLanding: 'request priority landing runway',
  emergencyDescent: 'commencing emergency descent, request priority landing runway',
  returnToLand: 'returning to the field, request landing runway',
};

/**
 * Picks the moment an emergency begins, from its own random stream so it cannot shift the traffic
 * of a seeded run. At most `maxActive` flights are in trouble at once.
 */
export class EmergencyGenerator {
  private nextAtS: number;
  snapshot(): number { return this.nextAtS; }
  restore(nextAtS: number): void { this.nextAtS = nextAtS; }
  constructor(private readonly rng: Rng, private readonly ratePerHour: number, private readonly maxActive = 1) {
    this.nextAtS = this.ratePerHour > 0 ? this.rng.exponentialS(this.ratePerHour) : Infinity;
  }

  /** The aircraft that should declare now, if any. */
  update(simTimeS: number, aircraft: readonly Aircraft[], airportElevationFt: number): { ac: Aircraft; nature: EmergencyNature } | null {
    if (simTimeS < this.nextAtS) return null;
    this.nextAtS = simTimeS + this.rng.exponentialS(this.ratePerHour);
    if (aircraft.filter((a) => a.emergency && !isRemovedPhase(a.phase)).length >= this.maxActive) return null;
    const candidates = aircraft.flatMap((ac) => eligibleNatures(ac, airportElevationFt).map((nature) => ({ ac, nature })));
    return candidates.length ? this.rng.pick(candidates) : null;
  }
}
